import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardMedia,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import DotCarousel from '../components/DotCarousel';
import { PaginatedFileList } from '../components/fileVisuals';
import FilePreviewDialog from '../components/FilePreviewDialog';
import type { PaginatedFileListItem } from '../components/fileVisuals';
import ProjectFiltersDialog from '../components/ProjectFiltersDialog';
import ProjectNavButtons from '../components/ProjectNavButtons';
import PageSearchBar from '../components/PageSearchBar';
import { PosterCard, type PosterItem } from '../components/PosterCard';
import { PaginatedPosterGrid } from '../components/PaginatedPosterGrid';
import { projectEssentialApi } from '../services/api';
import { logEbookPreviewed, logEbookDownloaded } from '../services/analytics';
import { resolveTitleBasedDownloadFilename } from '../../shared/utils/downloadFilename';
import type {
  InsightFileItem,
  EdmTabItem,
  LauncherEbookItem,
  ProjectFilters,
  QuarterlyReportItem,
} from '../services/api';
import { useAppliedFilterChips } from '../hooks/useAppliedFilterChips';
import { usePeFilters } from '../hooks/usePeFilters';
import { usePeSearchDropdown } from '../hooks/usePeSearchDropdown';
import SearchPendingPreviewHost from '../components/SearchPendingPreviewHost';
import {
  buildPeSearchPageHref,
  countPeFilters,
  parsePeSearchState,
} from '../utils/searchState';

// ---------------------------------------------------------------------------
// Placeholder data — frontend-only for now. The backend will fill these in
// later; the UI just mirrors the spec.
// ---------------------------------------------------------------------------

const MAX_UPLOADED_RECENTLY = 10;

interface DownloadableFile {
  id: string;
  filename: string;
  displayName: string;
  /** Short context line shown under the title (e.g. "Market Outlook & Analysis"). */
  subtitle?: string;
  /** Raw file size in bytes; rendered next to the type label. */
  sizeBytes?: number;
  uploadedAt: string;
  url: string;
  urlPublic?: string | null;
}

// Adapt a `DownloadableFile` to the row prop shape consumed by
// `PaginatedFileList`. Centralised so all three sections stay in sync.
function toFileListItems(files: DownloadableFile[]): PaginatedFileListItem[] {
  return files.map((f) => ({
    id: f.id,
    filename: f.filename,
    displayName: f.displayName,
    subtitle: f.subtitle,
    sizeBytes: f.sizeBytes,
    url: f.url,
    urlPublic: f.urlPublic ?? null,
    uploadedAt: f.uploadedAt,
    onPreview: () => logEbookPreviewed(f.id),
    onDownloadClick: () => logEbookDownloaded(f.id),
    forceBlobDownload: true,
    shareAsset: { id: f.id, kind: 'ebook' as const, title: f.displayName || f.id },
  }));
}

type UploadedSortOrder = 'asc' | 'desc';

function parseUploadedTime(value: string | null | undefined): number {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function sortByUploadedAt<T extends { uploadedAt?: string | null }>(
  items: T[],
  order: UploadedSortOrder,
): T[] {
  return [...items].sort((a, b) => {
    const diff = parseUploadedTime(b.uploadedAt) - parseUploadedTime(a.uploadedAt);
    return order === 'desc' ? diff : -diff;
  });
}



// Quarterly reports rendered as poster cards in a horizontal rail.
// Hydrated at runtime from `GET /quarterly-reports`, which pulls rows
// from the EDM table where Category IN ('QuarterlyReports', 'Reports'). The backend
// already maps EDM columns into the shape the UI consumes, so no
// further transformation is needed here.
type QuarterlyReport = QuarterlyReportItem;

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

// Plain section heading that matches the dashboard's `SectionHeader`: bold
// uppercase letterspaced text on the page background, optional trailing slot
// for a "View All" link or a chevron when used as a collapsible toggle.
function SectionHeader({
  title,
  collapsible,
  open,
  onToggle,
}: {
  title: string;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
}) {
  const interactive = !!collapsible && !!onToggle;
  return (
    <Box
      onClick={interactive ? onToggle : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={(e) => {
        if (!interactive) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle?.();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1.25,
        cursor: interactive ? 'pointer' : 'default',
        userSelect: 'none',
        '&:hover .section-title': interactive
          ? { color: 'var(--color-accent-strong)' }
          : undefined,
      }}
    >
      <Typography
        className="section-title"
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: 'var(--color-text-strong)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontSize: { xs: '0.95rem', sm: '1.05rem' },
          transition: 'color 120ms ease',
        }}
      >
        {title}
      </Typography>
      {collapsible && (
        <ExpandMoreIcon
          sx={{
            color: 'var(--color-text-muted)',
            transition: 'transform 200ms ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      )}
    </Box>
  );
}



function FileGroupHeader({
  count,
  sortOrder,
  onToggleSort,
}: {
  count: number;
  sortOrder?: UploadedSortOrder;
  onToggleSort?: () => void;
}) {
  const sortLabel =
    sortOrder === 'asc' ? 'Sort: Oldest ↑' : 'Sort: Newest ↓';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 0.5,
        py: 0.75,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        Files ({count})
      </Typography>
      {onToggleSort ? (
        <Button
          size="small"
          onClick={onToggleSort}
          sx={{
            minWidth: 0,
            px: 0.75,
            py: 0.25,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 700,
            fontSize: '0.7rem',
            color: 'var(--color-text-muted)',
            '&:hover': {
              color: 'var(--color-accent)',
              bgcolor: 'transparent',
            },
          }}
        >
          {sortLabel}
        </Button>
      ) : (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Sort: Newest ↓
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Tab content
// ---------------------------------------------------------------------------

function mapEdmEbookToPoster(row: EdmTabItem): PosterItem {
  return {
    id: row.id,
    title: row.title,
    date: row.createdAt
      ? new Date(row.createdAt).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '',
    cover: row.cover,
    downloads: row.downloads,
    url: row.url,
    urlPublic: row.urlPublic ?? null,
    filename: row.filename,
  };
}

function mapLauncherEbookToPoster(row: LauncherEbookItem): PosterItem {
  return {
    id: row.id,
    title: row.title,
    date: row.createdAt
      ? new Date(row.createdAt).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '',
    cover: row.cover,
    downloads: row.downloads,
    url: row.url,
    urlPublic: row.urlPublic ?? null,
    filename: row.filename ?? `${row.title || row.id}.pdf`,
  };
}

function FeaturedTab({
  filters,
  hideLatest,
  focusSection,
}: {
  filters: ProjectFilters;
  hideLatest: boolean;
  focusSection?: string | null;
}) {
  const filtersKey = [
    filters.regions?.join(',') ?? '',
    filters.districts?.join(',') ?? '',
    filters.propertyTypes?.join(',') ?? '',
    filters.projectIds?.join(',') ?? '',
  ].join('|');

  // ── Latest eBooks — client engagement + periodic, newest first ──
  const [recent, setRecent] = useState<PosterItem[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState<string | null>(null);
  useEffect(() => {
    if (hideLatest) {
      setRecent([]);
      setRecentLoading(false);
      setRecentError(null);
      return;
    }
    let mounted = true;
    setRecentLoading(true);
    setRecentError(null);
    Promise.all([
      projectEssentialApi.getEdmsByTab('ebooks', MAX_UPLOADED_RECENTLY, undefined, filters),
      projectEssentialApi.getLauncherEbooks(
        { variant: 'periodic' },
        MAX_UPLOADED_RECENTLY,
        null,
      ),
    ])
      .then(([edmRes, periodicRes]) => {
        if (!mounted) return;
        type MergeEntry = { createdAt: number; poster: PosterItem };
        const merged: MergeEntry[] = [
          ...edmRes.items.map((row) => ({
            createdAt: row.createdAt ? new Date(row.createdAt).getTime() : 0,
            poster: mapEdmEbookToPoster(row),
          })),
          ...periodicRes.items.map((row) => ({
            createdAt: row.createdAt ? new Date(row.createdAt).getTime() : 0,
            poster: mapLauncherEbookToPoster(row),
          })),
        ];
        merged.sort((a, b) => b.createdAt - a.createdAt);
        setRecent(merged.slice(0, MAX_UPLOADED_RECENTLY).map((entry) => entry.poster));
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        const msg =
          err instanceof Error ? err.message : 'Failed to load latest eBooks';
        setRecentError(msg);
        setRecent([]);
      })
      .finally(() => {
        if (mounted) setRecentLoading(false);
      });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filtersKey tracks filter identity
  }, [filtersKey, hideLatest]);

  // ── Upcoming Project EBooks — IsEbook EDMs on projects not yet launched ─
  const [upcoming, setUpcoming] = useState<PaginatedFileListItem[]>([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [upcomingError, setUpcomingError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setUpcomingLoading(true);
    setUpcomingError(null);
    projectEssentialApi
      .getLauncherEbooks({ variant: 'upcoming-projects' }, undefined, null, filters)
      .then((res) => {
        if (!mounted) return;
        setUpcoming(
          res.items.map((item) => ({
            id: item.id,
            filename: item.filename ?? `${item.title || item.id}.pdf`,
            displayName: item.title || item.id,
            subtitle: item.type || 'Client Engagement eBook',
            url: item.url,
            urlPublic: item.urlPublic ?? null,
            uploadedAt: item.createdAt,
            previewImage: item.cover,
            onPreview: () => logEbookPreviewed(item.id),
            onDownloadClick: () => logEbookDownloaded(item.id),
            forceBlobDownload: true,
            shareAsset: { id: item.id, kind: 'ebook' as const, title: item.title || item.id },
          }))
        );
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        const msg = err instanceof Error ? err.message : 'Failed to load upcoming eBooks';
        setUpcomingError(msg);
        setUpcoming([]);
      })
      .finally(() => {
        if (mounted) setUpcomingLoading(false);
      });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filtersKey tracks filter identity
  }, [filtersKey]);

  const filteredUpcoming = upcoming;

  const [upcomingSortOrder, setUpcomingSortOrder] = useState<UploadedSortOrder>('desc');

  const sortedFilteredUpcoming = useMemo(
    () => sortByUploadedAt(filteredUpcoming, upcomingSortOrder),
    [filteredUpcoming, upcomingSortOrder],
  );

  // ── Project Spotlight Ebooks — EDM rows with Category='Periodicebooks' ─
  // Project dimension filters must not narrow this section; search still
  // applies client-side below.
  const [periodic, setPeriodic] = useState<PaginatedFileListItem[]>([]);
  const [periodicLoading, setPeriodicLoading] = useState(true);
  const [periodicError, setPeriodicError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setPeriodicLoading(true);
    setPeriodicError(null);
    projectEssentialApi
      .getLauncherEbooks({ variant: 'periodic' }, undefined, null)
      .then((res) => {
        if (!mounted) return;
        setPeriodic(
          res.items.map((item) => ({
            id: item.id,
            filename: item.filename ?? `${item.title || item.id}.pdf`,
            displayName: item.title || item.id,
            subtitle: item.type || 'Project Spotlight Ebook',
            url: item.url,
            urlPublic: item.urlPublic ?? null,
            uploadedAt: item.createdAt,
            previewImage: item.cover,
            onPreview: () => logEbookPreviewed(item.id),
            onDownloadClick: () => logEbookDownloaded(item.id),
            forceBlobDownload: true,
            shareAsset: { id: item.id, kind: 'ebook' as const, title: item.title || item.id },
          }))
        );
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        const msg = err instanceof Error ? err.message : 'Failed to load Project Spotlight Ebooks';
        setPeriodicError(msg);
        setPeriodic([]);
      })
      .finally(() => {
        if (mounted) setPeriodicLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filteredPeriodic = periodic;

  const [periodicSortOrder, setPeriodicSortOrder] = useState<UploadedSortOrder>('desc');

  const sortedFilteredPeriodic = useMemo(
    () => sortByUploadedAt(filteredPeriodic, periodicSortOrder),
    [filteredPeriodic, periodicSortOrder],
  );

  // ── Client Engagement eBooks — live EDM rows where IsEbook is true ────────────────
  const [featured, setFeatured] = useState<PaginatedFileListItem[]>([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [featuredError, setFeaturedError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setFeaturedLoading(true);
    setFeaturedError(null);
    projectEssentialApi
      .getEdmsByTab('ebooks', undefined, undefined, filters)
      .then((res) => {
        if (!mounted) return;
        setFeatured(
          res.items.map((item) => ({
            id: item.id,
            filename: `${item.title || item.id}.pdf`,
            displayName: item.title || item.id,
            subtitle: item.category || 'Client Engagement eBook',
            url: item.url,
            urlPublic: item.urlPublic,
            uploadedAt: item.createdAt || '',
            // Render the curated thumbnail (EDM.Thumbnail) instead of
            // rasterising the PDF first page for every item.
            previewImage: item.cover,
            onPreview: () => logEbookPreviewed(item.id),
            onDownloadClick: () => logEbookDownloaded(item.id),
            forceBlobDownload: true,
            shareAsset: { id: item.id, kind: 'ebook' as const, title: item.title || item.id },
          }))
        );
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        const msg = err instanceof Error ? err.message : 'Failed to load Client Engagement eBooks';
        setFeaturedError(msg);
        setFeatured([]);
      })
      .finally(() => {
        if (mounted) setFeaturedLoading(false);
      });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filtersKey tracks filter identity
  }, [filtersKey]);

  const filteredFeatured = featured;

  const [featuredSortOrder, setFeaturedSortOrder] = useState<UploadedSortOrder>('desc');

  const sortedFilteredFeatured = useMemo(
    () => sortByUploadedAt(filteredFeatured, featuredSortOrder),
    [filteredFeatured, featuredSortOrder],
  );

  const [upcomingOpen, setUpcomingOpen] = useState(true);
  const [featuredOpen, setFeaturedOpen] = useState(
    () => focusSection !== 'periodic',
  );
  const [periodicOpen, setPeriodicOpen] = useState(
    () => focusSection !== 'client-engagement',
  );

  useEffect(() => {
    if (focusSection === 'periodic') {
      setPeriodicOpen(true);
      setFeaturedOpen(false);
    } else if (focusSection === 'client-engagement') {
      setFeaturedOpen(true);
      setPeriodicOpen(false);
    }
  }, [focusSection]);

  return (
    <Stack spacing={{ xs: 2.5, sm: 3 }}>
      {/* Latest rail is hidden while search/filters are active. */}
      {!hideLatest && (
      <Box>
        <SectionHeader title="Latest Ebooks" />
        {recentLoading && recent.length === 0 ? (
          // First-time load: show a spinner instead of an empty
          // carousel so the section reads as "loading" rather than
          // "nothing here".
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : recent.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', py: 2 }}>
            {recentError
              ? `Failed to fetch latest eBooks${
                  recentError === 'Failed to load latest eBooks' ? '' : `: ${recentError}`
                }.`
              : 'No highlights available.'}
          </Typography>
        ) : (
          <DotCarousel
            slides={recent.map((item) => ({
              key: item.id,
              node: (
                <PosterCard
                  item={item}
                  coverFit="contain"
                  shareKind="ebook"
                />
              ),
            }))}
          />
        )}
      </Box>
      )}

      {/* ─── Project Spotlight Ebooks (EDM Category='Periodicebooks') ─── */}
      <Box>
        <SectionHeader
          title="Project Spotlight Ebooks"
          collapsible
          open={periodicOpen}
          onToggle={() => setPeriodicOpen((v) => !v)}
        />
        <Collapse in={periodicOpen} timeout={200} unmountOnExit>
          <Box
            sx={{
              px: { xs: 0.5, sm: 1 },
              py: 1,
              bgcolor: 'var(--color-surface-elevated)',
              borderRadius: 1.5,
              border: '1px solid var(--color-border)',
            }}
          >
            {periodicLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : periodicError ? (
              <Typography
                variant="body2"
                sx={{ color: 'var(--color-text-muted)', px: 1.5, py: 2 }}
              >
                {periodicError}
              </Typography>
            ) : (
              <>
                <FileGroupHeader
                  count={sortedFilteredPeriodic.length}
                  sortOrder={periodicSortOrder}
                  onToggleSort={() =>
                    setPeriodicSortOrder((order) => (order === 'desc' ? 'asc' : 'desc'))
                  }
                />
                <PaginatedFileList
                  items={sortedFilteredPeriodic}
                  emptyLabel="No Project Spotlight Ebooks yet."
                />
              </>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* ─── Upcoming Projects (collapsible file kit) ─── */}
      <Box>
        <SectionHeader
          title="Upcoming Project EBooks"
          collapsible
          open={upcomingOpen}
          onToggle={() => setUpcomingOpen((v) => !v)}
        />
        <Collapse in={upcomingOpen} timeout={200} unmountOnExit>
          <Box
            sx={{
              px: { xs: 0.5, sm: 1 },
              py: 1,
              bgcolor: 'var(--color-surface-elevated)',
              borderRadius: 1.5,
              border: '1px solid var(--color-border)',
            }}
          >
            {upcomingLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : upcomingError ? (
              <Typography
                variant="body2"
                sx={{ color: 'var(--color-text-muted)', px: 1.5, py: 2 }}
              >
                {upcomingError}
              </Typography>
            ) : (
              <>
                <FileGroupHeader
                  count={sortedFilteredUpcoming.length}
                  sortOrder={upcomingSortOrder}
                  onToggleSort={() =>
                    setUpcomingSortOrder((order) => (order === 'desc' ? 'asc' : 'desc'))
                  }
                />
                <PaginatedFileList
                  items={sortedFilteredUpcoming}
                  emptyLabel="No upcoming-project eBooks yet."
                />
              </>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* ─── Featured Projects (collapsible curated eBooks) ─── */}
      <Box>
        <SectionHeader
          title="Client Engagement Ebooks"
          collapsible
          open={featuredOpen}
          onToggle={() => setFeaturedOpen((v) => !v)}
        />
        <Collapse in={featuredOpen} timeout={200} unmountOnExit>
          <Box
            sx={{
              px: { xs: 0.5, sm: 1 },
              py: 1,
              bgcolor: 'var(--color-surface-elevated)',
              borderRadius: 1.5,
              border: '1px solid var(--color-border)',
            }}
          >
            {featuredLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : featuredError ? (
              <Typography
                variant="body2"
                sx={{ color: 'var(--color-text-muted)', px: 2, py: 3, textAlign: 'center' }}
              >
                {featuredError}
              </Typography>
            ) : (
              <>
                <FileGroupHeader
                  count={sortedFilteredFeatured.length}
                  sortOrder={featuredSortOrder}
                  onToggleSort={() =>
                    setFeaturedSortOrder((order) => (order === 'desc' ? 'asc' : 'desc'))
                  }
                />
                <PaginatedFileList
                  items={sortedFilteredFeatured}
                  emptyLabel="No Client Engagement eBooks yet."
                />
              </>
            )}
          </Box>
        </Collapse>
      </Box>
    </Stack>
  );
}

function InsightsTab({
  focusSection,
}: {
  focusSection?: string | null;
}) {

  // Live insight files come from `GET /insights` — ResearchMaterial
  // rows with CategoryId = 'D2304002'. Same fetch-once-on-mount
  // pattern as the quarterly reports below.
  const [insights, setInsights] = useState<InsightFileItem[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setInsightsLoading(true);
    setInsightsError(null);
    projectEssentialApi
      .getInsights(50)
      .then((res) => {
        if (mounted) setInsights(res.items);
      })
      .catch((err: unknown) => {
        if (mounted) {
          const msg =
            err instanceof Error
              ? err.message
              : 'Failed to load insight files';
          setInsightsError(msg);
        }
      })
      .finally(() => {
        if (mounted) setInsightsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filteredInsights = useMemo<PaginatedFileListItem[]>(() => {
    const base: PaginatedFileListItem[] = insights.map((f) => ({
      id: f.id,
      filename: resolveTitleBasedDownloadFilename(f.displayName, f.filename, f.id),
      displayName: f.displayName,
      subtitle: f.subtitle,
      uploadedAt: f.uploadedAt ?? undefined,
      url: f.downloadLink || f.url,
      disablePreview: true,
      forceBlobDownload: true,
    }));
    return base;
  }, [insights]);

  const [insightsSortOrder, setInsightsSortOrder] = useState<UploadedSortOrder>('desc');

  const sortedFilteredInsights = useMemo(
    () => sortByUploadedAt(filteredInsights, insightsSortOrder),
    [filteredInsights, insightsSortOrder],
  );

  // Live quarterly reports come from `GET /quarterly-reports`. We fetch
  // once when the tab mounts and store the list locally; search
  // filtering happens client-side against the loaded set so the UX
  // stays consistent with the Insights & Analytics section above.
  const [reports, setReports] = useState<QuarterlyReport[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setReportsLoading(true);
    setReportsError(null);
    projectEssentialApi
      .getQuarterlyReports(50)
      .then((res) => {
        if (mounted) setReports(res.items);
      })
      .catch((err: unknown) => {
        if (mounted) {
          const msg =
            err instanceof Error
              ? err.message
              : 'Failed to load reports';
          setReportsError(msg);
        }
      })
      .finally(() => {
        if (mounted) setReportsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filteredReports = reports;

  const mappedReports = useMemo<PosterItem[]>(() => {
    return filteredReports.map((r) => {
      const dateLabel = r.createdAt
        ? new Date(r.createdAt).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
        : '';
      return {
        id: r.id,
        title: r.title,
        date: dateLabel,
        cover: r.cover,
        downloads: r.downloads,
        url: r.url,
        filename: r.filename || `${r.title}.pdf`,
      };
    });
  }, [filteredReports]);

  const [insightsOpen, setInsightsOpen] = useState(
    () => focusSection !== 'reports',
  );
  const [reportsOpen, setReportsOpen] = useState(
    () => focusSection !== 'analytics',
  );

  useEffect(() => {
    if (focusSection === 'reports') {
      setReportsOpen(true);
      setInsightsOpen(false);
    } else if (focusSection === 'analytics') {
      setInsightsOpen(true);
      setReportsOpen(false);
    }
  }, [focusSection]);

  return (
    <Stack spacing={{ xs: 2.5, sm: 3 }}>
      {/* ─── Insights & Analytics — file rows ─── */}
      <Box>
        <SectionHeader
          title="Insights & Analytics"
          collapsible
          open={insightsOpen}
          onToggle={() => setInsightsOpen((v) => !v)}
        />
        <Collapse in={insightsOpen} timeout={200} unmountOnExit>
          <Box
            sx={{
              px: { xs: 0.5, sm: 1 },
              py: 1,
              bgcolor: 'var(--color-surface-elevated)',
              borderRadius: 1.5,
              border: '1px solid var(--color-border)',
            }}
          >
            {insightsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : insightsError ? (
              <Typography
                variant="body2"
                sx={{ color: 'var(--color-text-muted)', px: 1.5, py: 2 }}
              >
                {insightsError}
              </Typography>
            ) : (
              <>
                <FileGroupHeader
                  count={sortedFilteredInsights.length}
                  sortOrder={insightsSortOrder}
                  onToggleSort={() =>
                    setInsightsSortOrder((order) => (order === 'desc' ? 'asc' : 'desc'))
                  }
                />
                <PaginatedFileList
                  items={sortedFilteredInsights}
                  emptyLabel="No insight files yet."
                />
              </>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* ─── Reports — poster rail ─── */}
      <Box>
        <SectionHeader
          title="Reports"
          collapsible
          open={reportsOpen}
          onToggle={() => setReportsOpen((v) => !v)}
        />
        <Collapse in={reportsOpen} timeout={200} unmountOnExit>
          <Box sx={{ mt: 1.5 }}>
            {reportsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : reportsError ? (
              <Typography
                variant="body2"
                sx={{ color: 'var(--color-text-muted)', py: 2 }}
              >
                {reportsError}
              </Typography>
            ) : (
              <PaginatedPosterGrid
                items={mappedReports}
                emptyLabel="No reports yet."
                onPreview={(item) => logEbookPreviewed(item.id)}
                onDownloadClick={(item) => logEbookDownloaded(item.id)}
                shareKind="report"
              />
            )}
          </Box>
        </Collapse>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function HighlightsAndEbookPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialSearchState = useMemo(
    () => parsePeSearchState(location.search),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.search],
  );
  const [tab, setTab] = useState<'featured' | 'insights'>(() =>
    initialSearchState.tab === 'insights' ? 'insights' : 'featured',
  );
  const [filters, setFilters] = usePeFilters();
  const { search, setSearch, dismissDropdownKey, dropdownContent } = usePeSearchDropdown(filters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [focusSection, setFocusSection] = useState<string | null>(
    initialSearchState.section ?? null,
  );
  useEffect(() => {
    setFocusSection(initialSearchState.section ?? null);
    if (initialSearchState.tab === 'insights') setTab('insights');
    else if (initialSearchState.tab === 'featured' || initialSearchState.section) {
      if (initialSearchState.tab !== 'insights') setTab('featured');
    }
  }, [initialSearchState]);

  const filterCount = countPeFilters(filters);
  const hideLatest = filterCount > 0;

  const { chips: appliedFilterChips, clearAll: clearAllFilters } = useAppliedFilterChips(
    filters,
    setFilters,
  );

  return (
    <Stack spacing={{ xs: 2, sm: 2.5 }}>
      <Box>
        <Button
          onClick={() => navigate('/project-essential')}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 999,
            borderColor: 'var(--color-border-strong)',
            color: 'var(--color-text-strong)',
            bgcolor: 'var(--color-surface)',
            '&:hover': {
              borderColor: 'var(--color-accent)',
              bgcolor: 'var(--color-accent-soft)',
            },
          }}
        >
          Back to Dashboard
        </Button>
      </Box>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: 'var(--color-accent)',
          textAlign: 'center',
          letterSpacing: '0.01em',
          fontSize: { xs: '1.6rem', sm: '2rem', md: '2.25rem' },
        }}
      >
        Highlights &amp; eBook
      </Typography>

      <PageSearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search projects, EDMs, eBooks and reports…"
        onSubmit={(query) => navigate(buildPeSearchPageHref(query, filters))}
        filterCount={filterCount}
        onOpenFilters={() => setFiltersOpen(true)}
        appliedFilters={appliedFilterChips}
        onClearAllFilters={clearAllFilters}
        dropdownContent={dropdownContent}
        dismissDropdownKey={dismissDropdownKey}
      />

      <ProjectNavButtons active="highlights" />

      <Box sx={{ borderBottom: '1px solid var(--color-border)' }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { xs: '0.9rem', sm: '0.95rem' },
              minHeight: 40,
              px: { xs: 2, sm: 3 },
              color: 'var(--color-text-muted)',
              '&.Mui-selected': { color: 'var(--color-accent-strong)' },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'var(--color-accent)',
              height: 3,
              borderRadius: 2,
            },
          }}
        >
          <Tab value="featured" label="Projects" />
          <Tab value="insights" label="Insights" />
        </Tabs>
      </Box>

      {tab === 'featured' ? (
        <FeaturedTab
          filters={filters}
          hideLatest={hideLatest}
          focusSection={focusSection}
        />
      ) : (
        <InsightsTab focusSection={focusSection} />
      )}

      <ProjectFiltersDialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onApply={(next) => {
          navigate(buildPeSearchPageHref(search.trim(), next));
        }}
      />

      <SearchPendingPreviewHost />
    </Stack>
  );
}
